import { useQuery } from '@tanstack/react-query';

import type { GetMarkPickupScheduledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get pickup scheduled status from a purchased vehicle
 */
export const getMarkPickupScheduled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkPickupScheduledResponse> => {
	const response = await apiClient.get<GetMarkPickupScheduledResponse>(
		ordersEndpoints.markPickupScheduled(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting pickup scheduled status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkPickupScheduled = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markPickupScheduled.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get pickup scheduled status without an order ID.');
			}
			return getMarkPickupScheduled(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
